import { useMutation, useQuery } from '@apollo/client';
import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router-dom';

import Button from '../../../core/components/Button.tsx';
import CheckboxGroup from '../../../core/components/CheckboxGroup.tsx';
import StringField from '../../../core/components/Fields/StringField.tsx';
import FormField from '../../../core/components/FormField.tsx';
import Layout from '../../../core/components/Layout.tsx';
import { GET_NAVIGATION_CHANNELS } from '../../../core/components/Navigation/internal/queries.ts';
import Page from '../../../core/components/Page/index.tsx';
import SaveStatus from '../../../core/components/SaveStatus.tsx';
import SettingsFormCard from '../../../core/components/SettingsFormCard.tsx';
import SettingsNavigation from '../../../core/components/SettingsNavigation.tsx';
import { useToaster } from '../../../core/components/Toast.tsx';
import countFieldErrors from '../../../core/utils/countFieldErrors.ts';
import { url } from '../../../core/utils/link.ts';
import IntegrationBreadcrumb from '../../components/IntegrationBreadcrumb.tsx';
import { CUSTOMER_INTEGRATION_BUSINESS_CENTRAL_PATH } from '../../constants.ts';
import { GET_BUSINESS_CENTRAL_INTEGRATIONS, UPDATE_BUSINESS_CENTRAL_INTEGRATION } from '../../queries.ts';

const FORM_DEFAULT_VALUES = {
    name: '',
    businessCentralUrl: '',
    orderChannelIds: [],
    orderConfirmationChannelIds: [],
};

const BusinessCentralIntegrationEditPage = () => {
    const { t } = useTranslation('integrations');
    const { integrationId } = useParams();
    const navigate = useNavigate();
    const { publishToast } = useToaster();
    const backTooltipText = `${t('backTooltipText')} ${t('erp.pageTitle')} / ${t('businessCentral.title')}`;

    const { data, refetch } = useQuery(GET_BUSINESS_CENTRAL_INTEGRATIONS, {
        fetchPolicy: 'no-cache',
        notifyOnNetworkStatusChange: false,
    });
    const integration = data?.businessCentralIntegrations?.find((i) => i.id === integrationId);

    const { data: channelData } = useQuery(GET_NAVIGATION_CHANNELS, {
        fetchPolicy: 'network-only',
        notifyOnNetworkStatusChange: false,
    });
    const orderChannels = channelData?.orderProcessingChannels || [];
    const orderConfirmationChannels = channelData?.orderConfirmationProcessingChannels || [];

    const form = useForm({ defaultValues: FORM_DEFAULT_VALUES, mode: 'onTouched' });
    const formErrors = form.formState.errors;

    useEffect(() => {
        if (integration) {
            form.reset({
                ...FORM_DEFAULT_VALUES,
                name: integration.name,
                businessCentralUrl: integration.businessCentralUrl,
                orderChannelIds: integration.orderChannels.map((c) => c.id),
                orderConfirmationChannelIds: integration.orderConfirmationChannels.map((c) => c.id),
            });
        }
    }, [integration]);

    const [updateIntegration, { loading: isSubmitting }] = useMutation(UPDATE_BUSINESS_CENTRAL_INTEGRATION);

    const handleSubmit = async (formData) => {
        try {
            await updateIntegration({
                variables: {
                    id: integration.id,
                    name: formData.name,
                    businessCentralUrl: formData.businessCentralUrl,
                    orderChannels: formData.orderChannelIds,
                    orderConfirmationChannels: formData.orderConfirmationChannelIds,
                },
            });
            await refetch();
            publishToast({ description: t('businessCentral.updateSuccess'), status: 'success' });
            navigate(url(CUSTOMER_INTEGRATION_BUSINESS_CENTRAL_PATH));
        } catch (error) {
            console.error(error);
            publishToast({ description: t('businessCentral.updateError'), status: 'error' });
        }
    };

    return (
        <Layout>
            <SettingsNavigation />

            <Page className="flex-1">
                <Page.Header>
                    <IntegrationBreadcrumb
                        backTooltip={backTooltipText}
                        items={[
                            { label: t('businessCentral.title'), to: url(CUSTOMER_INTEGRATION_BUSINESS_CENTRAL_PATH) },
                            { label: integration?.name || t('businessCentral.form.updateTitle') },
                        ]}
                    />
                </Page.Header>

                <Page.Content lowered className="flex flex-col gap-6">
                    <SettingsFormCard
                        onSubmit={form.handleSubmit(handleSubmit)}
                        title={t('businessCentral.form.updateTitle')}
                    >
                        <Controller
                            name="name"
                            control={form.control}
                            rules={{ required: t('businessCentral.form.nameRequired') }}
                            render={({ field }) => (
                                <FormField label={t('businessCentral.form.name')} error={formErrors.name} required>
                                    <StringField {...field} />
                                </FormField>
                            )}
                        />

                        <Controller
                            name="businessCentralUrl"
                            control={form.control}
                            rules={{
                                required: t('businessCentral.form.businessCentralUrlRequired'),
                                validate: (value) => {
                                    try {
                                        new URL(value);
                                        return true;
                                    } catch (e) {
                                        return t('businessCentral.form.businessCentralUrlInvalid');
                                    }
                                },
                            }}
                            render={({ field }) => (
                                <FormField
                                    label={t('businessCentral.form.businessCentralUrl')}
                                    error={formErrors.businessCentralUrl}
                                    required
                                >
                                    <StringField {...field} />
                                </FormField>
                            )}
                        />

                        <Controller
                            name="orderChannelIds"
                            control={form.control}
                            render={({ field }) => (
                                <FormField
                                    label={t('businessCentral.form.orderChannelIds')}
                                    error={formErrors.orderChannelIds}
                                >
                                    <CheckboxGroup
                                        {...field}
                                        options={orderChannels.map((c) => ({ value: c.id, label: c.name }))}
                                        onValueChange={(value) => field.onChange({ target: { value } })}
                                    />
                                </FormField>
                            )}
                        />

                        <Controller
                            name="orderConfirmationChannelIds"
                            control={form.control}
                            render={({ field }) => (
                                <FormField
                                    label={t('businessCentral.form.orderConfirmationChannelIds')}
                                    error={formErrors.orderConfirmationChannelIds}
                                >
                                    <CheckboxGroup
                                        {...field}
                                        options={orderConfirmationChannels.map((c) => ({ value: c.id, label: c.name }))}
                                        onValueChange={(value) => field.onChange({ target: { value } })}
                                    />
                                </FormField>
                            )}
                        />

                        <SaveStatus
                            isDirty={form.formState.isDirty}
                            isSubmitting={isSubmitting}
                            errorCount={countFieldErrors(form.formState.errors)}
                        >
                            <Button type="button" asChild>
                                <Link to={url(CUSTOMER_INTEGRATION_BUSINESS_CENTRAL_PATH)}>
                                    {t('businessCentral.form.cancelButton')}
                                </Link>
                            </Button>
                        </SaveStatus>
                    </SettingsFormCard>
                </Page.Content>
            </Page>
        </Layout>
    );
};

export default BusinessCentralIntegrationEditPage;
